import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        error: '',
        records: action.payload.products || [],
        totalRecords: action.payload.countProducts || 0,
        totalPages: action.payload.pages || 1,
        currentPage: action.payload.page || 1,
        summary: action.payload.summary || {},
        settings: action.payload.settings || state.settings,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

const monthOptionsFallback = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
].map((label, index) => ({ value: index + 1, label }));

const fmtNum = (value, digits = 0) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const isCurrentMonthRecord = (record) => {
  const now = new Date();
  return Number(record?.year) === now.getFullYear() && Number(record?.monthN) === now.getMonth() + 1;
};

const statusInfo = (record) => {
  if (record && !isCurrentMonthRecord(record)) {
    return { label: 'Paid', icon: 'fa-check-circle', bg: '#d1e7dd', color: '#0f5132' };
  }
  const status = record?.workflowStatus || (record?.status ? 'distributed' : 'draft');
  if (status === 'distributed' || record?.status) {
    return { label: 'Paid', icon: 'fa-check-circle', bg: '#d1e7dd', color: '#0f5132' };
  }
  if (status === 'calculated') {
    return { label: 'Ready to Pay', icon: 'fa-calculator', bg: '#dbeafe', color: '#1d4ed8' };
  }
  if (status === 'cancelled') {
    return { label: 'Cancelled', icon: 'fa-ban', bg: '#f8d7da', color: '#842029' };
  }
  return { label: 'Draft', icon: 'fa-pencil', bg: '#fff3cd', color: '#664d03' };
};

export default function GlobalScreen() {
  const [{ loading, error, records, totalPages, settings }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    records: [],
    totalRecords: 0,
    totalPages: 1,
    currentPage: 1,
    summary: {},
    settings: { pointRate: 59.75, months: monthOptionsFallback, statuses: [] },
  });

  const { state } = useContext(Store);
  const { userInfo } = state;
  const autoRefreshRef = useRef('');
  const [autoPhase, setAutoPhase] = useState('');
  const [autoError, setAutoError] = useState('');
  const [autoTargetId, setAutoTargetId] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rateRows, setRateRows] = useState([]);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateSaving, setRateSaving] = useState(false);
  const [rateError, setRateError] = useState('');
  const [rateMessage, setRateMessage] = useState('');
  const navigate = useNavigate();
  const { search } = useLocation();
  const sp = useMemo(() => new URLSearchParams(search), [search]);
  const page = parseInt(sp.get('page')) || 1;
  const status = sp.get('status') || 'all';
  const month = sp.get('month') || '';
  const year = sp.get('year') || '';
  const pageSize = sp.get('pageSize') || '20';

  const headers = useMemo(() => ({
    Authorization: `Bearer ${userInfo.token}`,
  }), [userInfo.token]);

  const loadData = useCallback(async () => {
    try {
      dispatch({ type: 'FETCH_REQUEST' });
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('pageSize', pageSize);
      if (status && status !== 'all') params.set('status', status);
      if (month) params.set('month', month);
      if (year) params.set('year', year);

      const { data } = await axios.get(
        api + `/api/costs/admin/global?${params.toString()}`,
        { headers }
      );
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  }, [headers, month, page, pageSize, status, year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadRateSettings = useCallback(async () => {
    try {
      setRateLoading(true);
      setRateError('');
      const { data } = await axios.get(api + '/api/costs/admin/global/designation-rates', { headers });
      setRateRows((data.rates || []).map((item) => ({
        ...item,
        pointRate: String(item.pointRate ?? 0),
      })));
      setRateMessage(data.message || '');
    } catch (err) {
      setRateError(getError(err));
    } finally {
      setRateLoading(false);
    }
  }, [headers]);

  const openRateSettings = () => {
    setSettingsOpen(true);
    setRateMessage('');
    loadRateSettings();
  };

  const saveRateSettings = async () => {
    try {
      setRateSaving(true);
      setRateError('');
      const payload = {
        rates: rateRows.map((row) => ({
          serial: row.serial,
          pointRate: Number(row.pointRate || 0),
        })),
      };
      const { data } = await axios.put(api + '/api/costs/admin/global/designation-rates', payload, { headers });
      setRateRows((data.rates || []).map((item) => ({
        ...item,
        pointRate: String(item.pointRate ?? 0),
      })));
      setRateMessage(data.message || 'Designation rates saved.');
      await loadData();
    } catch (err) {
      setRateError(getError(err));
    } finally {
      setRateSaving(false);
    }
  };

  const autoRefreshLastRow = useCallback(async (recordId) => {
    try {
      setAutoError('');
      setAutoPhase('users');
      await axios.post(api + `/api/costs/admin/global/${recordId}/refresh-users`, {}, { headers });
      setAutoPhase('calculate');
      await axios.post(api + `/api/costs/admin/global/${recordId}/calculate`, {}, { headers });
      setAutoPhase('results');
      await loadData();
    } catch (err) {
      setAutoError(getError(err));
    } finally {
      setAutoPhase('');
    }
  }, [headers, loadData]);

  useEffect(() => {
    if (loading || records.length === 0) return;

    const isRecordFinal = (record) => {
      const status = record?.workflowStatus || (record?.status ? 'distributed' : 'draft');
      return (
        Boolean(record?.status)
        || status === 'distributed'
        || status === 'cancelled'
        || Boolean(record?.isLocked)
        || !isCurrentMonthRecord(record)
      );
    };

    const targetRecord = records.find((record) => record?._id && !isRecordFinal(record));
    if (!targetRecord?._id) return;

    const pageKey = `${search}|${targetRecord._id}`;
    if (autoRefreshRef.current === pageKey) return;
    autoRefreshRef.current = pageKey;
    setAutoTargetId(targetRecord._id);
    autoRefreshLastRow(targetRecord._id);
  }, [autoRefreshLastRow, loading, records, search]);

  const updateFilter = (next) => {
    const params = new URLSearchParams(search);
    Object.entries(next).forEach(([key, value]) => {
      if (value === '' || value === 'all' || value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    params.set('page', '1');
    navigate(`/admin/global?${params.toString()}`);
  };

  const goPage = (nextPage) => {
    const params = new URLSearchParams(search);
    params.set('page', String(nextPage));
    navigate(`/admin/global?${params.toString()}`);
  };

  const months = settings.months && settings.months.length ? settings.months : monthOptionsFallback;
  const statuses = settings.statuses && settings.statuses.length
    ? settings.statuses
    : [
      { value: 'all', label: 'All' },
      { value: 'draft', label: 'Draft' },
      { value: 'calculated', label: 'Ready to Pay' },
      { value: 'distributed', label: 'Paid' },
    ];

  return (
    <div style={s.page}>
      <Helmet><title>Global Sales Control</title></Helmet>

      <div style={s.header}>
        <div>
          <p style={s.eyebrow}>Admin Control</p>
          <h4 style={s.title}>Global Sales</h4>
          <p style={s.subtitle}>Current month opens automatically. Review qualified users, adjust safely, and pay with confidence.</p>
        </div>
        <div style={s.headerActions}>
          <button type="button" style={s.secondaryBtn} onClick={openRateSettings}>
            <i className="fa fa-cog" />
            Settings
          </button>
          <button type="button" style={s.secondaryBtn} onClick={loadData} disabled={loading}>
            <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
            Refresh List
          </button>
        </div>
      </div>

      <div style={s.filters}>
        <select value={status} onChange={(e) => updateFilter({ status: e.target.value })} style={s.input}>
          {statuses.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
        <select value={month} onChange={(e) => updateFilter({ month: e.target.value })} style={s.input}>
          <option value="">All Months</option>
          {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <input value={year} onChange={(e) => updateFilter({ year: e.target.value })} style={s.input} type="number" min="2000" placeholder="Year" />
        <select value={pageSize} onChange={(e) => updateFilter({ pageSize: e.target.value })} style={s.input}>
          <option value="10">10 rows</option>
          <option value="20">20 rows</option>
          <option value="50">50 rows</option>
          <option value="100">100 rows</option>
        </select>
        <button type="button" style={s.secondaryBtn} onClick={() => navigate('/admin/global')}>
          <i className="fa fa-refresh" /> Clear
        </button>
      </div>

      <div style={s.tablePanel}>
        {autoPhase ? <AutoRefreshSkeleton phase={autoPhase} /> : null}
        {autoError ? (
          <div style={s.autoError}>
            <i className="fa fa-exclamation-triangle" />
            <span>Auto refresh could not complete: {autoError}</span>
          </div>
        ) : null}
        {loading ? (
          <State icon="fa-spinner fa-spin" title="Loading global sales..." />
        ) : error ? (
          <State icon="fa-exclamation-triangle" title="Could not load global sales" text={error} danger />
        ) : records.length === 0 ? (
          <State icon="fa-inbox" title="No global month found" text="Refresh the page. The current month is created automatically." />
        ) : (
          <>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Month</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Joined Users</th>
                    <th style={s.th}>Rate</th>
                    <th style={s.th}>Global Amount</th>
                    <th style={s.th}>Qualified Users</th>
                    <th style={s.th}>Control</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, index) => {
                    const meta = statusInfo(record);
                    const totalAchievers = record.liveTotalAchievers ?? record.totalAchievers ?? 0;
                    const isAutoRefreshingRow = index === 0 && record._id === autoTargetId && Boolean(autoPhase);
                    return (
                      <tr key={record._id} style={{ ...s.tr, ...(isAutoRefreshingRow ? s.processingRow : null) }}>
                        <td style={s.td}>
                          <div style={s.monthCell}>
                            <div style={s.monthIcon}>{String(record.month || '').slice(0, 3).toUpperCase()}</div>
                            <div>
                              <div style={s.monthName}>{record.month} {record.year}</div>
                              <div style={s.smallText}>
                                {record.userCountSource === 'manual' ? 'Manual count' : 'Auto count'}{record.isLocked ? ' - Locked' : ''}
                                {isAutoRefreshingRow ? ' - Auto refreshing...' : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={s.td}><Badge meta={meta} /></td>
                        <td style={s.td}>
                          {isAutoRefreshingRow ? <span style={s.inlineCellSkeleton} aria-hidden="true" /> : fmtNum(record.users)}
                        </td>
                        <td style={s.td}>
                          {isAutoRefreshingRow ? <span style={{ ...s.inlineCellSkeleton, width: 62 }} aria-hidden="true" /> : fmtNum(record.pointRate, 2)}
                        </td>
                        <td style={s.td}>
                          {isAutoRefreshingRow ? <span style={{ ...s.inlineCellSkeleton, width: 116 }} aria-hidden="true" /> : <strong>{fmtNum(record.totalPool, 2)}</strong>}
                        </td>
                        <td style={s.td}>
                          {isAutoRefreshingRow ? <span style={{ ...s.inlineCellSkeleton, width: 72 }} aria-hidden="true" /> : fmtNum(totalAchievers)}
                        </td>
                        <td style={s.td}>
                          <div style={s.actions}>
                            <Link to={`distribute/${record._id}`} style={s.actionBtn} title="Open month">
                              <i className="fa fa-folder-open" />
                              Open
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={s.pagination}>
                <button type="button" style={s.secondaryBtn} disabled={page <= 1} onClick={() => goPage(page - 1)}>
                  <i className="fa fa-angle-left" /> Previous
                </button>
                <span style={s.pageText}>Page {page} of {totalPages}</span>
                <button type="button" style={s.secondaryBtn} disabled={page >= totalPages} onClick={() => goPage(page + 1)}>
                  Next <i className="fa fa-angle-right" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {settingsOpen ? (
        <div style={s.modalOverlay} role="dialog" aria-modal="true">
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <div>
                <h5 style={s.modalTitle}>Designation Point Rates</h5>
                <p style={s.modalText}>Only point rates can be changed. Draft months pick up new rates the next time you calculate them; calculated, paid and locked months keep their saved rates.</p>
              </div>
              <button type="button" style={s.closeBtn} onClick={() => setSettingsOpen(false)} aria-label="Close">×</button>
            </div>

            {rateLoading ? (
              <State icon="fa-spinner fa-spin" title="Loading designation rates..." />
            ) : rateError ? (
              <div style={s.rateError}><i className="fa fa-exclamation-triangle" /> {rateError}</div>
            ) : (
              <div style={s.rateTableWrap}>
                <table style={s.rateTable}>
                  <thead>
                    <tr>
                      <th style={s.th}>Designation</th>
                      <th style={s.th}>Required Match</th>
                      <th style={s.th}>Point Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateRows.map((row, index) => (
                      <tr key={row._id || row.serial}>
                        <td style={s.td}>
                          <strong>{row.name}</strong>
                          <div style={s.smallText}>Serial {row.serial}</div>
                        </td>
                        <td style={s.td}>{fmtNum(row.requiredMatch)}</td>
                        <td style={s.td}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.pointRate}
                            onChange={(e) => {
                              const next = [...rateRows];
                              next[index] = { ...next[index], pointRate: e.target.value };
                              setRateRows(next);
                            }}
                            style={{ ...s.input, width: 140 }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {rateMessage ? <div style={s.rateInfo}><i className="fa fa-info-circle" /> {rateMessage}</div> : null}
            <div style={s.formActions}>
              <button type="button" style={s.secondaryBtn} onClick={() => setSettingsOpen(false)}>Close</button>
              <button type="button" style={s.primaryBtn} onClick={saveRateSettings} disabled={rateLoading || rateSaving}>
                <i className={`fa ${rateSaving ? 'fa-spinner fa-spin' : 'fa-save'}`} />
                Save Rates
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AutoRefreshSkeleton({ phase }) {
  const messages = {
    users: 'Checking joined users for the last row...',
    calculate: 'Calculating qualified users for the last row...',
    results: 'Loading refreshed totals...',
  };

  return (
    <div style={s.autoSkeleton} role="status" aria-live="polite">
      <div style={s.autoSkeletonStatus}>
        <i className="fa fa-refresh fa-spin" />
        <span>{messages[phase] || 'Auto refreshing last row...'}</span>
      </div>
      <div style={s.autoSkeletonGrid} aria-hidden="true">
        <span style={s.autoSkeletonCard} />
        <span style={s.autoSkeletonCard} />
        <span style={s.autoSkeletonCard} />
        <span style={s.autoSkeletonCard} />
      </div>
    </div>
  );
}

function Badge({ meta }) {
  return (
    <span style={{ ...s.badge, background: meta.bg, color: meta.color }}>
      <i className={`fa ${meta.icon}`} />
      {meta.label}
    </span>
  );
}

function State({ icon, title, text, danger }) {
  return (
    <div style={s.emptyState}>
      <i className={`fa ${icon}`} style={{ color: danger ? '#dc3545' : '#0d6efd', fontSize: 30 }} />
      <h6 style={{ margin: '14px 0 4px', color: danger ? '#dc3545' : '#1f2937' }}>{title}</h6>
      {text && <p style={{ margin: 0, color: '#6b7280' }}>{text}</p>}
    </div>
  );
}

const s = {
  page: {
    padding: 20,
    maxWidth: 1240,
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    color: '#111827',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 16,
  },
  headerActions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' },
  eyebrow: { margin: 0, fontSize: 12, fontWeight: 700, color: '#0d6efd', textTransform: 'uppercase' },
  title: { margin: '2px 0', fontSize: 28, fontWeight: 800 },
  subtitle: { margin: 0, color: '#6b7280', fontSize: 14 },
  stepRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
    marginBottom: 16,
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#fff',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 12,
    marginBottom: 16,
  },
  stat: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 16,
  },
  statIcon: (color) => ({
    width: 42,
    height: 42,
    borderRadius: 8,
    background: `${color}18`,
    color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
  }),
  statLabel: { color: '#6b7280', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' },
  statValue: { fontSize: 22, fontWeight: 800 },
  formPanel: {
    background: '#fff',
    border: '1px solid #dbeafe',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
  },
  panelTitle: { margin: 0, fontSize: 16, fontWeight: 800 },
  helpPill: {
    padding: '5px 10px',
    borderRadius: 999,
    background: '#eef2ff',
    color: '#3730a3',
    fontSize: 12,
    fontWeight: 700,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    color: '#374151',
    fontSize: 13,
    fontWeight: 700,
  },
  input: {
    height: 40,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '0 12px',
    fontSize: 14,
    background: '#fff',
    color: '#111827',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 10,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  tablePanel: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    overflow: 'hidden',
  },
  autoSkeleton: {
    margin: 12,
    padding: 14,
    borderRadius: 8,
    border: '1px solid #bfdbfe',
    background: '#f8fbff',
  },
  autoSkeletonStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#1d4ed8',
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 10,
  },
  autoSkeletonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 8,
  },
  autoSkeletonCard: {
    height: 34,
    borderRadius: 6,
    background: '#dbeafe',
  },
  autoError: {
    margin: '0 12px 12px',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #fecaca',
    background: '#fff5f5',
    color: '#b91c1c',
    fontSize: 13,
    fontWeight: 700,
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 1040 },
  th: {
    textAlign: 'left',
    padding: '13px 14px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
  },
  td: { padding: '14px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' },
  tr: { background: '#fff' },
  processingRow: { background: '#f8fbff' },
  inlineCellSkeleton: {
    display: 'inline-block',
    width: 94,
    height: 14,
    borderRadius: 999,
    background: '#dbeafe',
  },
  monthCell: { display: 'flex', alignItems: 'center', gap: 10 },
  monthIcon: {
    width: 46,
    height: 42,
    borderRadius: 8,
    background: '#eff6ff',
    color: '#1d4ed8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 800,
  },
  monthName: { fontWeight: 800, color: '#111827' },
  smallText: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  actions: { display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' },
  actionBtn: {
    minHeight: 34,
    borderRadius: 8,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '7px 10px',
    fontSize: 12,
    fontWeight: 800,
    cursor: 'pointer',
    textDecoration: 'none',
  },
  primaryBtn: {
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    background: '#0d6efd',
    color: '#fff',
    fontWeight: 800,
    fontSize: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    minHeight: 40,
  },
  secondaryBtn: {
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '9px 13px',
    background: '#fff',
    color: '#374151',
    fontWeight: 800,
    fontSize: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    minHeight: 40,
    textDecoration: 'none',
  },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 14,
  },
  pageText: { color: '#6b7280', fontWeight: 700 },
  emptyState: { textAlign: 'center', padding: '70px 20px' },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(17, 24, 39, 0.45)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modal: {
    width: '100%',
    maxWidth: 760,
    maxHeight: '88vh',
    overflowY: 'auto',
    background: '#fff',
    borderRadius: 8,
    padding: 18,
    boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
  },
  modalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 },
  modalTitle: { margin: 0, fontSize: 18, fontWeight: 800 },
  modalText: { margin: '4px 0 0', color: '#6b7280', fontSize: 13 },
  closeBtn: {
    border: 'none',
    background: '#f3f4f6',
    color: '#374151',
    width: 34,
    height: 34,
    borderRadius: 8,
    fontSize: 22,
    cursor: 'pointer',
    lineHeight: 1,
  },
  rateTableWrap: { overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 },
  rateTable: { width: '100%', borderCollapse: 'collapse', minWidth: 560 },
  rateError: {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #fecaca',
    background: '#fff5f5',
    color: '#b91c1c',
    fontSize: 13,
    fontWeight: 700,
  },
  rateInfo: {
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #bfdbfe',
    background: '#eff6ff',
    color: '#1d4ed8',
    fontSize: 13,
    fontWeight: 700,
  },
  checkboxRow: { display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 40 },
};
